import { AlertHexagon, AlertTriangle, Check, InfoCircle, Link01, LinkBroken02 } from '@aktiia/design-system';
import cx from 'classnames';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SyncState } from '../../../enums';
import { getPatientSyncState, getPatientSyncStateText } from '../../../features/patients';
import { ISyncStatusProps } from './types';

export const SyncStatus = ({ patient, showSubtitle = false }: ISyncStatusProps) => {
  const { t } = useTranslation();

  const state = useMemo(() => getPatientSyncState(patient), [patient]);

  const icon = useMemo(() => {
    switch (state) {
      case SyncState.CONNECTED:
        return <Link01 className="scale-75" />;
      case SyncState.DISCONNECTED:
        return <LinkBroken02 className="scale-75 text-text-negative" />;
      case SyncState.VALID:
        return <Check className="scale-75" />;
      case SyncState.WARNING:
        return <InfoCircle className="scale-75" />;
      case SyncState.ALERT:
        return <AlertTriangle className="scale-75" />;
      case SyncState.ERROR:
        return <AlertHexagon className="scale-75" />;
    }
  }, [state]);

  const title = useMemo(() => getPatientSyncStateText(patient), [patient]);

  const subtitle = useMemo(() => {
    const baseKey = 'common.syncState.subtitle';
    switch (state) {
      case SyncState.CONNECTED:
        return t(`${baseKey}.connected`);
      case SyncState.DISCONNECTED: {
        const date = format(patient.stopSharingDate ?? 0, 'd MMM yyyy');
        return t(`${baseKey}.connected`, { date });
      }
      case SyncState.VALID:
      case SyncState.WARNING: {
        const date = format(patient.bloodPressure.nextInitialization ?? 0, 'd MMM yyyy');
        return t(`${baseKey}.nextInitialization`, { date });
      }
      case SyncState.ALERT: {
        const date = format(patient.bloodPressure.lastDateForMeasurements ?? 0, 'd MMM yyyy');
        return t(`${baseKey}.validUntil`, { date });
      }
      case SyncState.ERROR: {
        const date = format(patient.bloodPressure.lastDateForMeasurements ?? 0, 'd MMM yyyy');
        return t(`${baseKey}.expiredAt`, { date });
      }
    }
  }, [patient, state]);

  return (
    <>
      <p className={cx('flex items-center', { 'mb-1': showSubtitle })}>
        {icon && <span className="mr-2">{icon}</span>}
        <span className="typography-inline-highlight-3 text-text-primary">{title}</span>
      </p>
      {showSubtitle && subtitle && (
        <p className="typography-paragraph-4 text-text-secondary whitespace-pre-line">{subtitle}</p>
      )}
    </>
  );
};
